/**
 * Presentation Layer - RegistrationResultAlert Component
 * 登録結果アラートコンポーネント
 */

import React from 'react';
import { Alert, AlertTitle, Box, Typography, List, ListItem, ListItemText } from '@mui/material';
import type { RegistrationResult } from '../../../../domain/medicalReservation/RegistrationResult';

/**
 * Props定義
 */
export interface RegistrationResultAlertProps {
  /** 登録結果 */
  result: RegistrationResult | null;
}

/**
 * 登録結果アラートコンポーネント
 */
export const RegistrationResultAlert: React.FC<RegistrationResultAlertProps> = ({
  result,
}) => {
  if (!result) {
    return null;
  }

  return (
    <Alert
      severity={result.success ? 'success' : 'error'}
      sx={{ my: 2 }}
    >
      <AlertTitle>
        {result.success ? '登録完了' : '登録エラー'}
      </AlertTitle>

      {/* メッセージ */}
      <Typography variant="body2">{result.message}</Typography>

      {/* 詳細情報（成功時） */}
      {result.success && result.details && (
        <Box sx={{ mt: 1 }}>
          <List dense disablePadding>
            <ListItem disablePadding>
              <ListItemText
                primary={`成功: ${result.details.successCount} 件`}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItem>
            {result.details.failureCount > 0 && (
              <ListItem disablePadding>
                <ListItemText
                  primary={`失敗: ${result.details.failureCount} 件`}
                  primaryTypographyProps={{ variant: 'body2', color: 'error' }}
                />
              </ListItem>
            )}
            {result.details.duplicateCount > 0 && (
              <ListItem disablePadding>
                <ListItemText
                  primary={`重複: ${result.details.duplicateCount} 件`}
                  primaryTypographyProps={{ variant: 'body2', color: 'warning.main' }}
                />
              </ListItem>
            )}
          </List>
        </Box>
      )}

      {/* エラー詳細（失敗時） */}
      {!result.success && result.errors && result.errors.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="error" sx={{ fontWeight: 'medium' }}>
            エラー詳細:
          </Typography>
          <List dense disablePadding>
            {result.errors.slice(0, 5).map((error, index) => (
              <ListItem key={index} disablePadding>
                <ListItemText
                  primary={`行 ${error.row}: ${error.message}`}
                  primaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
            {result.errors.length > 5 && (
              <ListItem disablePadding>
                <ListItemText
                  primary={`... 他 ${result.errors.length - 5} 件のエラー`}
                  primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                />
              </ListItem>
            )}
          </List>
        </Box>
      )}
    </Alert>
  );
};
